/**
 *
 * CreateParentCommunication
 *
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import history from 'utils/history';

// styles
import './style.scss';

// layout components
import Preloader from 'components/Preloader';
import ErrorBoundary from 'engine/ErrorBoundary';

// cta notifications
import FinalScreen from 'mobile/components/FinalScreen';
import StepByStepWrapper from 'mobile/components/StepByStepWrapper';
// inner components
import { CommTypeForm, RequestCommForm } from './components';

// contexts
import { useGetLocalUser } from 'engine/Contexts/User.context';

// utils
import { clean } from 'utils/helpers/helper.general';
// hooks
import { useRefState } from 'utils/hooks';

function CreateParentCommunication({
  // main
  student,
  // sates
  loading,
  successful,
  // actions
  onGoBack,
  onSave,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'create-parent-communication-wrapper',
    className,
  );

  const localUser = useGetLocalUser()();

  const [step, setStep] = useState(1);
  const [totalStep, setTotalStep] = useState(2);
  const [showFinal, setShowFinal] = useState(false);

  // absence, custom
  const [communicationType, setCommunicationType] = useState(false);
  const [currentCommunication, currentCommunicationRef, setCurrentCommunication] = useRefState(false);

  // default form options
  let formOptions = {
    formData: currentCommunicationRef?.current || null,
    currentStep: step,
    // Step Navigation
    nextStep: () => step != totalStep && setStep(step + 1),
    prevStep: () => {
      return step > 1 && setStep(step - 1);
    },
    goToStep: number => setStep(number),
    result: values => onSaveHandler(values),
  };

  // if 'communicationType' is equal to 'absence' then redirect to creaction of absence
  useEffect(() => {
    if (communicationType == 'absence') {
      history.push({
        pathname: `/absences`,
        state: {
          openAbsence: 'no-medical',
        },
      });
    }
  }, [communicationType]);


  const onSaveHandler = useCallback((resultData) => {
    if (resultData?.type) {
      setCommunicationType(resultData?.type);
    }

    const newObj = { ...currentCommunication, ...resultData };

    setCurrentCommunication(newObj);
    
    if (step == totalStep) {
      console.debug('Last Step!!! Submit!');
      const { type, ...restResult } = newObj;
      setShowFinal(true);
      !!onSave && onSave(restResult);
    }
    
  }, [onSave, step, totalStep, currentCommunication]);

  const finalNotification = useMemo(() => { 
    return (
      <FinalScreen
        title="Votre demande a été transmise"
        continueAction={onGoBack}
        loading={loading}
        successful={successful}
      >
        {`L’information a été transmise à l’établissement scolaire de
      l’étudiant(e). <br /> <br /> En cas d’absence prolongée, vous receverez une notification
      afin de confirmer que l’élève est toujours malade, dès demain.`}
      </FinalScreen>
    )
  }, [onGoBack, loading, successful]);

  const generateSteps = useCallback(stepNumber => {
    switch (stepNumber) {
      case 1:
        return (
          <StepByStepWrapper
            number={1}
            isActive={stepNumber == 1}
            header="Motif de la requête"
            title="Votre demande concerne"
          >
            <CommTypeForm {...formOptions} />
          </StepByStepWrapper>
        );
      case 2:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={2}
                isActive={stepNumber == 2}
                header={`Nouvelle requête`}
                title="Autre requête"
              >
                <RequestCommForm {...formOptions} loading={loading} successful={successful} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
    }
  }, [showFinal, formOptions, loading, successful]);

  return localUser?.role == 'parent' && (
    <div className={childClassNames}>
      <ErrorBoundary>
        {generateSteps(step)}
      </ErrorBoundary>
    </div>
  );
}

CreateParentCommunication.defaultProps = {
  loading: false,
  successful: false
};
CreateParentCommunication.propTypes = {
  onGoBack: PropTypes.func,
  onSave: PropTypes.func,
};

export default CreateParentCommunication;
